import { useTranslation } from 'react-i18next';

import Tooltip, { TooltipRows } from '@/components/atoms/Tooltip';
import { formatDate } from '@/utils/date';

import styles from './LocationOverviewFishGroupCell.module.scss';

/** Same placeholder the rest of the table uses when a value is missing. */
const NO_VALUE = '---';

interface FishGroupNameProps {
    name: string;
    className?: string;
}

function FishGroupName(props: FishGroupNameProps) {
    return <span className={props.className}>{props.name}</span>;
}

export interface LocationOverviewFishGroupCellProps {
    /** Cohort name, or null/undefined when the pen has none. */
    name?: string | null;
    /** Arrival in this pen. */
    fromDate?: string | null;
    /** Departure from this pen, or null while the cohort is still there. */
    toDate?: string | null;
    /**
     * The pen is one of the location's historical pens (`historicalSince` set).
     * Only then does a missing cohort get a label; any other pen shows `---`.
     */
    historicalCage?: boolean;
    /** What this location calls a pen, so a smolt site's placeholder says tank. */
    penLabel?: 'common.labels.cage' | 'common.labels.tank';
}

/**
 * A cohort's name, with the span of its stay in this pen in the tooltip.
 *
 * Takes the fields rather than a cage so both row kinds can use it: a live pen
 * passes its active cohort, a cohort row passes itself. Keeping one component is
 * what stops the two from formatting the same dates differently.
 *
 * The dates are the stay in *this* pen, not the cohort's lifecycle: a relocated
 * cohort was born before it arrived here, and the pen's charts only cover the
 * part it spent here.
 */
export default function LocationOverviewFishGroupCell(props: LocationOverviewFishGroupCellProps) {
    const {
        name,
        fromDate,
        toDate,
        historicalCage = false,
        penLabel = 'common.labels.cage',
    } = props;
    const { t } = useTranslation('fishGroups');

    if (!name) {
        // A historical pen has no cohort by design, so say so rather than leave a
        // blank that reads like missing data. Rendered through the same tooltip
        // machinery as a real name, so both states are positioned alike.
        if (historicalCage) {
            const isTank = penLabel === 'common.labels.tank';
            // A plain sentence, like the link buttons' tooltips in this table. `TooltipRows`
            // is for the key/value rows below.
            return (
                <Tooltip
                    content={t(
                        isTank
                            ? 'locationOverview.historicalTankTooltip'
                            : 'locationOverview.historicalCageTooltip'
                    )}
                    layout="fit">
                    <FishGroupName
                        name={t(
                            isTank
                                ? 'locationOverview.historicalTankLabel'
                                : 'locationOverview.historicalCageLabel'
                        )}
                        className={styles.historicalCage}
                    />
                </Tooltip>
            );
        }

        return <>{NO_VALUE}</>;
    }

    // `display` is `dd/MM/yyyy`, the same as the Last-measurement column, so the
    // dates in one row read alike.
    const from = formatDate(fromDate, 'display');
    const to = formatDate(toDate, 'display');

    const tooltipRows = [
        {
            key: t('locationOverview.periodLabel'),
            // An open stay reads as "from X" rather than inventing an end date;
            // "until today" would claim the cohort is measured up to now, which is
            // a different statement.
            value: to ? `${from} – ${to}` : t('locationOverview.sinceValue', { date: from }),
        },
    ];

    // Portalled into `document.body`, like the swim-speed cell's: it neither adds to the
    // overflow of the table's scroll container nor gets clipped by it, and Popper flips the
    // bubble when there is no room above the row.
    return (
        <Tooltip content={<TooltipRows rows={tooltipRows} />} layout="fit">
            <FishGroupName name={name} />
        </Tooltip>
    );
}
